import {
  DelayedRender,
  MultiSectionLayout,
  PoweredByJuglLabel,
} from "@jugl-web/ui-kit";
import { ProgressMessages } from "@jugl-web/ui-kit/feedback/ProgressMessages";
import { cx } from "@jugl-web/utils";
import { useDialogState } from "@jugl-web/utils/hooks/useDialogState";
import { CountryCode, isSupportedCountry } from "libphonenumber-js";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useChatMessageListScrollHandler } from "../../hooks/useChatMessageListScrollHandler";
import { useScrollToLastMessage } from "../../hooks/useScrollToLastMessage";
import { ChatBotUIProduct, ChatBotUIProps, ChatBotUIRef } from "../../types";
import { ChatBotInfoMessageComponent } from "../ChatBotInfoMessageComponent";
import { ChatBotMessageComponent } from "../ChatBotMessageComponent";
import { ChatInput } from "../ChatInput";
import { ChatMessagesLoadError } from "../ChatMessagesLoadError";
import { ChatProductDetailsDrawer } from "../ChatProductDetailsDrawer/ChatProductDetailsDrawer";
import { ChatReconnectingBanner } from "../ChatReconnectingBanner";
import { ChatSuggestionPills } from "../ChatSuggestionPills";
import { DelayedTypingIndicatorBubble } from "../TypingIndicatorBubble/TypingIndicatorBubble";
import { VoiceModePanel } from "../VoiceModePanel";
import { ChatHeader } from "./components/ChatHeader";
import {
  FloatingAlertManager,
  type FloatingAlertManagerHandle,
} from "./components/FloatingAlertManager";

export const ChatBotUI = forwardRef<ChatBotUIRef, ChatBotUIProps>(
  (
    {
      messages,
      suggestions = [],
      config,
      voiceMode,
      visitorInfo,
      isConnected = true,
      isInitialLoading = false,
      isReconnecting = false,
      isSending = false,
      isLoadingMore = false,
      isExpanded = false,
      isSoundOn = true,
      isTyping = false,
      isInputDisabled = false,
      error,
      onSendMessage,
      onSetVoiceMode,
      onReachEnd,
      onRetryLoad,
      onExpandToggle,
      onSoundToggle,
      onRestart,
      onClose,
      getMessageActions,
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState("");

    const [
      productDetailsDrawerState,
      openProductDetailsDrawer,
      closeProductDetailsDrawer,
    ] = useDialogState<{ product: ChatBotUIProduct | null }>({
      isOpen: false,
      data: { product: null },
    });

    const messageListRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const floatingAlertManagerRef = useRef<FloatingAlertManagerHandle | null>(
      null
    );

    const { refs, scrollToLastMessage } = useScrollToLastMessage({
      containerRef: messageListRef,
    });

    useChatMessageListScrollHandler({
      isInitialized: !isInitialLoading,
      isVisible: !voiceMode,
      containerRef: messageListRef,
      onReachEnd,
    });

    const shouldShowReconnectingBanner = isReconnecting && !isInitialLoading;
    const areSuggestionsVisible = suggestions.length > 0 && !isInitialLoading;

    // Expose imperative methods
    useImperativeHandle(ref, () => ({
      clearInput: () => setInputValue(""),
      setInput: (value: string) => setInputValue(value),
      getInput: () => inputValue,
      scrollToLastMessage,
      focusInput: () => {
        inputRef.current?.focus();
      },
      enqueueAlert: (options) => {
        floatingAlertManagerRef.current?.enqueueAlert(options);
      },
    }));

    const handleSendMessage = useCallback(() => {
      const text = inputValue.trim();

      if (!text) return;

      onSendMessage({ type: "text", text });
      setInputValue("");
      inputRef.current?.focus();
    }, [inputValue, onSendMessage]);

    const handleInputChange = useCallback((value: string) => {
      setInputValue(value);
    }, []);

    return (
      <div className="relative h-full min-h-0 w-full min-w-0 bg-white">
        <MultiSectionLayout
          className="max-w-full min-w-0 overflow-x-hidden"
          classNames={{
            middle: "min-w-0",
            content: "min-w-0 overflow-x-hidden",
          }}
          header={
            <ChatHeader
              title={config.headerTitle}
              imageUrl={config.chatImageUrl}
              isExpanded={isExpanded}
              isSoundOn={isSoundOn}
              onExpandToggle={onExpandToggle}
              onSoundToggle={onSoundToggle}
              onRestart={onRestart}
              onClose={onClose}
            />
          }
          footer={
            voiceMode ? undefined : (
              <PoweredByJuglLabel className="animate-fade-in" />
            )
          }
        >
          {/* Chat Content */}
          <div className="relative h-full min-h-0 w-full overflow-hidden">
            <FloatingAlertManager ref={floatingAlertManagerRef} />

            {voiceMode ? (
              <VoiceModePanel
                className="mx-auto animate-fade-in pb-[38px]"
                {...voiceMode}
              />
            ) : (
              <div className="relative flex h-full w-full animate-fade-in flex-col overflow-hidden font-secondary">
                {shouldShowReconnectingBanner && <ChatReconnectingBanner />}

                {/* Message List */}
                <div
                  ref={messageListRef}
                  className="jugl__custom-scrollbar relative flex flex-1 flex-col gap-3 overflow-y-auto px-3 py-4"
                >
                  {/* Initial Loading State */}
                  {isInitialLoading && (
                    <DelayedRender
                      isLoading
                      delayMs={500}
                      loadingContent={
                        <div className="flex flex-1 items-center justify-center">
                          <ProgressMessages
                            messages={[
                              "Preparing your chat",
                              "Setting things up",
                              "Almost there",
                            ]}
                            loop={false}
                            className="font-secondary text-sm text-[#707577]"
                          />
                        </div>
                      }
                    />
                  )}

                  {/* Error State */}
                  {error && !isInitialLoading && (
                    <ChatMessagesLoadError onRetry={onRetryLoad} />
                  )}

                  {/* Loading More Indicator */}
                  {isLoadingMore && messages.length > 0 && (
                    <div className="flex justify-center py-2">
                      <span className="rounded-full bg-white px-4 py-2 font-secondary text-sm text-[#707577] shadow-md">
                        Loading older messages...
                      </span>
                    </div>
                  )}

                  {!isInitialLoading && (
                    <>
                      {/* Messages */}
                      {messages.map((message, index) => {
                        const isLast = index === messages.length - 1;
                        const isSecondToLast =
                          messages.length > 1 && index === messages.length - 2;
                        const isOutgoing = message.type === "outgoing";

                        if (message.type === "info") {
                          return (
                            <ChatBotInfoMessageComponent
                              ref={(el) => {
                                if (isOutgoing)
                                  refs.outgoingMessage.current = el;
                                if (isLast) refs.lastMessage.current = el;
                                if (isSecondToLast)
                                  refs.secondToLastMessage.current = el;
                              }}
                              key={message.id}
                              message={message}
                            />
                          );
                        }

                        const actions = getMessageActions?.(
                          message,
                          index,
                          messages
                        );

                        return (
                          <ChatBotMessageComponent
                            ref={(el) => {
                              if (isOutgoing) refs.outgoingMessage.current = el;
                              if (isLast) refs.lastMessage.current = el;
                              if (isSecondToLast)
                                refs.secondToLastMessage.current = el;
                            }}
                            key={message.id}
                            message={message}
                            isLast={isLast}
                            userBubbleColor={config.userBubbleColor}
                            defaultCountryCode={
                              visitorInfo?.location
                                ? isSupportedCountry(
                                    visitorInfo.location.toUpperCase()
                                  )
                                  ? (visitorInfo.location.toUpperCase() as CountryCode)
                                  : undefined
                                : undefined
                            }
                            onSendMessage={onSendMessage}
                            onOpenProductDetailsDrawer={(product) =>
                              openProductDetailsDrawer({ product })
                            }
                            {...actions}
                          />
                        );
                      })}

                      {isTyping && (
                        <DelayedTypingIndicatorBubble
                          userBubbleColor={config.userBubbleColor}
                          agentName={config.agentName}
                          agentImageUrl={config.agentImageUrl}
                        />
                      )}
                    </>
                  )}
                </div>

                {/* Input Area */}
                <div
                  className={cx(
                    "px-4",
                    areSuggestionsVisible &&
                      "border-t border-r-0 border-b-0 border-l-0 border-solid border-[#F2F2F2]"
                  )}
                >
                  {areSuggestionsVisible && (
                    <ChatSuggestionPills
                      suggestions={suggestions}
                      className="py-4"
                      onSelect={(suggestion) =>
                        onSendMessage({ type: "text", text: suggestion })
                      }
                    />
                  )}
                  <ChatInput
                    ref={inputRef}
                    value={inputValue}
                    onChange={handleInputChange}
                    onSend={handleSendMessage}
                    onEnterVoiceMode={
                      onSetVoiceMode ? () => onSetVoiceMode(true) : undefined
                    }
                    placeholder={config.inputPlaceholder}
                    textSize={config.inputTextSize}
                    isDisabled={
                      !isConnected ||
                      isInitialLoading ||
                      isSending ||
                      isInputDisabled
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </MultiSectionLayout>
        <ChatProductDetailsDrawer
          isOpen={productDetailsDrawerState.isOpen}
          product={productDetailsDrawerState.data.product}
          onAddToCart={(product, variant, quantity) => {
            onSendMessage({
              type: "add-to-cart",
              data: { product, variant, quantity },
            });
          }}
          positionStrategy="absolute"
          layoutMode="fullscreen"
          onClose={closeProductDetailsDrawer}
        />
      </div>
    );
  }
);

ChatBotUI.displayName = "ChatBotUI";
